/**
 * SYNTHETIC TEST DATA ONLY
 *
 * Preview seed for the staff app shell. Neutral fixture names only — no real
 * clinic brands, no dubbed.app data. Codes are sequential preview ids.
 * No patients, no PHI, no copied rows.
 */

export const CONTEXT_STORAGE_PREFIX = 'dubbed.staff.app.';

export const CONTEXT_KEYS = {
  persona: 'dubbed.staff.app.persona',
  locationId: 'dubbed.staff.app.locationId',
} as const;

export type PreviewPersona = 'admin' | 'owner';

export interface SeedLocation {
  readonly id: string;
  readonly clinicId: string;
  readonly name: string;
  readonly code: string;
}

export interface SeedActor {
  readonly id: string;
  readonly persona: PreviewPersona;
  readonly displayName: string;
  readonly roleTitle: string;
  /** `all` = platform admin sees every seed location. */
  readonly locationIds: 'all' | readonly string[];
}

export const SEED_LOCATIONS: readonly SeedLocation[] = [
  {
    id: 'location-000010',
    clinicId: 'clinic-000010',
    name: 'Location Alpha',
    code: 'LOC-AAA-001',
  },
  {
    id: 'location-000011',
    clinicId: 'clinic-000011',
    name: 'Location Bravo',
    code: 'LOC-BBB-001',
  },
  {
    id: 'location-000012',
    clinicId: 'clinic-000012',
    name: 'Location Charlie',
    code: 'LOC-CCC-001',
  },
];

export const SEED_ACTORS: Record<PreviewPersona, SeedActor> = {
  admin: {
    id: 'staff-user-000010',
    persona: 'admin',
    displayName: 'Preview Admin',
    roleTitle: 'Platform admin',
    locationIds: 'all',
  },
  owner: {
    id: 'staff-user-000011',
    persona: 'owner',
    displayName: 'Preview Location Owner',
    roleTitle: 'Owner',
    locationIds: ['location-000010'],
  },
};

export function isPreviewPersona(value: unknown): value is PreviewPersona {
  return value === 'admin' || value === 'owner';
}

export function locationsForActor(actor: SeedActor): readonly SeedLocation[] {
  if (actor.locationIds === 'all') {
    return SEED_LOCATIONS;
  }
  const allowed = new Set(actor.locationIds);
  return SEED_LOCATIONS.filter((location) => allowed.has(location.id));
}

export function findSeedLocation(id: string): SeedLocation | undefined {
  return SEED_LOCATIONS.find((location) => location.id === id);
}

export function filterLocations(
  locations: readonly SeedLocation[],
  query: string,
): readonly SeedLocation[] {
  const needle = query.trim().toLowerCase();
  if (!needle) {
    return locations;
  }
  return locations.filter(
    (location) =>
      location.name.toLowerCase().includes(needle) ||
      location.code.toLowerCase().includes(needle),
  );
}

export const CONTEXT_CHANGE_EVENT = 'dubbed:staff-context';

export interface StaffAppContext {
  readonly actor: SeedActor;
  readonly location: SeedLocation;
  readonly locations: readonly SeedLocation[];
}

export function hasStaffAppContext(storage: Storage = sessionStorage): boolean {
  return isPreviewPersona(storage.getItem(CONTEXT_KEYS.persona));
}

export function readStaffAppContext(storage: Storage = sessionStorage): StaffAppContext | null {
  const persona = storage.getItem(CONTEXT_KEYS.persona);
  if (!isPreviewPersona(persona)) {
    return null;
  }
  const actor = SEED_ACTORS[persona];
  const locations = locationsForActor(actor);
  const requested = storage.getItem(CONTEXT_KEYS.locationId);
  const location =
    (requested ? locations.find((item) => item.id === requested) : undefined) ?? locations[0];
  if (!location) {
    return null;
  }
  return { actor, location, locations };
}

export function startStaffApp(
  persona: PreviewPersona,
  storage: Storage = sessionStorage,
): StaffAppContext {
  const actor = SEED_ACTORS[persona];
  const locations = locationsForActor(actor);
  const location = locations[0];
  if (!location) {
    throw new Error('seed locations are empty');
  }
  storage.setItem(CONTEXT_KEYS.persona, persona);
  storage.setItem(CONTEXT_KEYS.locationId, location.id);
  return { actor, location, locations };
}

export function selectStaffLocation(
  locationId: string,
  storage: Storage = sessionStorage,
): StaffAppContext | null {
  const current = readStaffAppContext(storage);
  if (!current) {
    return null;
  }
  const next = current.locations.find((item) => item.id === locationId);
  if (!next) {
    return current;
  }
  storage.setItem(CONTEXT_KEYS.locationId, next.id);
  return { ...current, location: next };
}

export function switchStaffPersona(
  persona: PreviewPersona,
  storage: Storage = sessionStorage,
): StaffAppContext {
  const actor = SEED_ACTORS[persona];
  const locations = locationsForActor(actor);
  const previousId = storage.getItem(CONTEXT_KEYS.locationId);
  const location =
    (previousId ? locations.find((item) => item.id === previousId) : undefined) ?? locations[0];
  if (!location) {
    throw new Error('seed locations are empty');
  }
  storage.setItem(CONTEXT_KEYS.persona, persona);
  storage.setItem(CONTEXT_KEYS.locationId, location.id);
  return { actor, location, locations };
}

export function clearStaffAppContext(storage: Storage = sessionStorage): void {
  const keys: string[] = [];
  for (let index = 0; index < storage.length; index += 1) {
    const key = storage.key(index);
    if (key?.startsWith(CONTEXT_STORAGE_PREFIX)) {
      keys.push(key);
    }
  }
  for (const key of keys) {
    storage.removeItem(key);
  }
}

export function notifyStaffContextChanged(): void {
  window.dispatchEvent(new Event(CONTEXT_CHANGE_EVENT));
}

export function locationLabel(location: SeedLocation): string {
  return `${location.name} · ${location.code}`;
}
